import React from "react";
import { fmt } from "@/lib/format";
import { TID } from "@/lib/testIds";
import { SectionTitle } from "@/components/FinancialCharts";
import { Users } from "lucide-react";

export default function CompetitorTable({ data }) {
  const revActual = (data.financials.revenue || []).filter((q) => !q.is_forecast);
  const niActual = (data.financials.net_income || []).filter((q) => !q.is_forecast);
  const gmActual = (data.financials.gross_margin || []).filter((q) => !q.is_forecast);
  const ttmRevenue = revActual.reduce((a, b) => a + (b.value || 0), 0);
  const ttmNet = niActual.reduce((a, b) => a + (b.value || 0), 0);
  const lastGM = gmActual.slice(-1)[0]?.value ?? 0;
  const rows = [
    {
      ticker: data.ticker,
      name: data.name_zh || data.name,
      market_cap: data.market_cap,
      revenue: ttmRevenue,
      gross_margin: lastGM,
      net_margin: ttmRevenue > 0 ? (ttmNet / ttmRevenue) * 100 : 0,
      eps: data.eps,
      pe: data.pe,
      roe: data.roe,
      position: "本次分析標的",
      strength: (data.advantages?.[0]?.title) || "",
      risk: (data.risks?.short?.[0]?.title) || "",
      isSelf: true,
    },
    ...(data.competitors || []).map((c) => ({ ...c, isSelf: false })),
  ];

  return (
    <section>
      <SectionTitle icon={Users} title="競爭對手比較" subtitle="Peer Table" />

      <div className="card-tactical overflow-hidden" data-testid={TID.competitorTable}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-zinc-900/70 border-b border-zinc-800">
                {[
                  "公司",
                  "市值",
                  "營收 (B)",
                  "毛利率",
                  "淨利率",
                  "EPS",
                  "P/E",
                  "ROE",
                  "市場定位",
                  "核心優勢 / 風險",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left px-3 py-2.5 text-[10px] font-medium text-zinc-500 uppercase tracking-widest whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.ticker}
                  className={`border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors ${
                    r.isSelf ? "bg-blue-500/5" : ""
                  }`}
                >
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-white">{r.ticker}</span>
                      {r.isSelf && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-widest bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          本股
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{r.name}</div>
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-100">{fmt.bigMoney(r.market_cap)}</td>
                  <td className="px-3 py-3 font-mono text-zinc-100">{fmt.num(r.revenue, 1)}</td>
                  <td className="px-3 py-3 font-mono text-emerald-400">
                    {r.gross_margin != null ? `${fmt.num(r.gross_margin, 1)}%` : "—"}
                  </td>
                  <td className="px-3 py-3 font-mono">
                    {r.net_margin != null ? (
                      <span className={r.net_margin < 0 ? "text-red-400" : "text-zinc-100"}>
                        {fmt.num(r.net_margin, 1)}%
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-100">{fmt.num(r.eps)}</td>
                  <td className="px-3 py-3 font-mono text-zinc-100">
                    {r.pe != null ? fmt.num(r.pe, 1) : "—"}
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-100">
                    {r.roe != null ? `${fmt.num(r.roe, 1)}%` : "—"}
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-300 max-w-[160px]">
                    {r.position}
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-400 max-w-[220px]">
                    <div className="text-emerald-400">✓ {r.strength || "—"}</div>
                    <div className="text-red-400 mt-0.5">△ {r.risk || "—"}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-950/50 text-xs sm:text-sm text-zinc-300 leading-relaxed">
          <span className="text-white font-semibold">AI 總結：</span>{" "}
          {peerVerdict(data)}
        </div>
      </div>
    </section>
  );
}

function peerVerdict(data) {
  const selfPE = data.pe || 0;
  const peerPEs = (data.competitors || []).map((c) => c.pe).filter((v) => v != null);
  const avgPE = peerPEs.length ? peerPEs.reduce((a, b) => a + b, 0) / peerPEs.length : selfPE;
  const val =
    selfPE > avgPE * 1.2 ? "估值明顯高於同業" : selfPE > avgPE ? "估值略高於同業" : "估值低於同業平均";
  const gmActual = (data.financials.gross_margin || []).filter((q) => !q.is_forecast);
  const selfGM = gmActual.slice(-1)[0]?.value ?? 0;
  const peerGMs = (data.competitors || []).map((c) => c.gross_margin);
  const avgGM = peerGMs.length ? peerGMs.reduce((a, b) => a + b, 0) / peerGMs.length : selfGM;
  const margin =
    selfGM > avgGM + 5
      ? "毛利率明顯優於同業"
      : selfGM > avgGM
      ? "毛利率略優於同業"
      : "毛利率低於同業平均";
  return `${data.name_zh || data.name} 相對主要競爭對手，${margin}，${val}。整體財務體質在同業中${
    data.ai_score.overall >= 80 ? "處於前段班" : data.ai_score.overall >= 65 ? "處於中段" : "略顯落後"
  }。`;
}
